import BackButton from 'components/backButton'
import { Button, Dropdown, FormLayout, TextInput } from 'components/common'
import { useAuth } from 'hooks'
import { useEffect, useMemo } from 'react'
import { SubmitHandler, UseFormReturn } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { User } from 'types/user'
import {
  PAGE_STORE_ORDERS,
  PAGE_USER_MGMT,
  PROTECTED_PAGES,
  ROLES,
} from 'utils/constants'
import StoreDropdown from './storeDropdown'
import { PAGE_ACCESS_KEY_PREFIX } from './utils'

interface UserFormProps {
  isUpdatePage?: boolean
  submitButtonText: string
  submitHandler: SubmitHandler<any>
  hookForm: UseFormReturn<any, any, undefined>
  defaultUser?: User
}

const UserForm = ({
  submitHandler,
  isUpdatePage = false,
  submitButtonText = 'Submit',
  hookForm,
  defaultUser,
}: UserFormProps) => {
  const { user } = useAuth()

  const {
    reset,
    watch,
    handleSubmit,
    register,
    setValue,
    getValues,
    formState: { errors },
  } = hookForm

  const navigate = useNavigate()

  useEffect(() => {
    if (defaultUser) {
      const pageAccessKeyValue: Record<string, boolean> = {}
      defaultUser.pageAccess?.forEach((page) => {
        pageAccessKeyValue[`PAGE_${page.page}`] = page.isEditor
      })
      reset({ ...defaultUser, ...pageAccessKeyValue })
    }
  }, [defaultUser])
  // to watch the change of role dropdown
  const role = watch('role')

  // Determine if the user is a `super admin` based on their role
  const isSuperAdmin = user.role === ROLES.SUPER_ADMIN

  // options for Page access Permissions
  const pageAccessPermissionOptions = useMemo(() => {
    // initial all options
    let options = PROTECTED_PAGES

    // options for Admin user or dropdown selected for admin
    if (!isSuperAdmin || role === ROLES.ADMIN) {
      options = PROTECTED_PAGES.filter((page) => page.id !== PAGE_USER_MGMT.id)
    }

    return options.map((page) => ({
      label: page.name,
      value: page.id,
      disabled: false,
    }))
  }, [isSuperAdmin, role])

  // options for User roles
  const rolesOptions = useMemo(
    () => [
      { label: 'Admin', value: ROLES.ADMIN, disabled: !isSuperAdmin },
      {
        label: 'Super Admin',
        value: ROLES.SUPER_ADMIN,
        disabled: !isSuperAdmin,
      },
    ],
    [isSuperAdmin],
  )

  const permissionOptions = [
    { label: 'No Access', value: null, disabled: false },
    { label: 'Editor', value: true, disabled: false },
    {
      label: 'Viewer',
      value: false,
      disabled: false,
    },
  ]

  // To return back to list
  const handleBack = () => {
    navigate(PAGE_USER_MGMT.path)
    reset()
  }

  // Store Order Page select watcher
  const storeOrderPage = watch(PAGE_ACCESS_KEY_PREFIX + PAGE_STORE_ORDERS.id)
  const showStoreDropdown = typeof storeOrderPage === 'boolean'

  return (
    <section>
      <BackButton handleBack={handleBack} />
      <FormLayout>
        <form
          className="w-full max-w-4xl 2xl:max-w-4xl"
          onSubmit={handleSubmit(submitHandler)}
        >
          <div className="grid grid-cols-8 gap-10 md:divide-x-2">
            <div className="col-span-8 md:col-span-3 ">
              <div>
                <TextInput
                  required={!isUpdatePage}
                  label="First Name"
                  register={register}
                  name="firstName"
                  placeholder="Enter your First Name"
                  error={errors?.firstName?.message as string}
                />
              </div>

              {/* Last Name */}

              <TextInput
                required={!isUpdatePage}
                label="Last Name"
                register={register}
                name="lastName"
                placeholder="Enter your Last Name"
                error={errors?.lastName?.message as string}
              />

              {/* Employee ID */}
              <TextInput
                required={!isUpdatePage}
                label="Employee ID"
                register={register}
                name="empId"
                placeholder="Enter your Employee ID"
                error={errors?.empId?.message as string}
                disabled={!!isUpdatePage}
              />

              {/* Email */}
              <TextInput
                required={!isUpdatePage}
                label="Email"
                register={register}
                name="email"
                placeholder="Enter your Email"
                error={errors?.email?.message as string}
                disabled={!!isUpdatePage}
              />

              {/* Password */}
              {isSuperAdmin && (
                <TextInput
                  required={!isUpdatePage}
                  label="Password"
                  register={register}
                  name="password"
                  type="password"
                  hintText="Atleast 8 characters, capital letter, small letter and symbol"
                  placeholder="Enter Password here"
                  error={errors?.password?.message as string}
                />
              )}

              {/* Role */}
              <Dropdown
                label="Role"
                setValue={setValue}
                name="role"
                defaultValue={defaultUser?.role}
                error={errors?.role?.message as string}
                options={rolesOptions}
              />

              {/* Account Status */}
              <Dropdown
                label="Account Status"
                setValue={setValue}
                name="isActive"
                options={[
                  { label: 'Active', value: true, disabled: false },
                  { label: 'Inactive', value: false, disabled: false },
                ]}
                defaultValue={defaultUser?.isActive}
                error={errors?.isActive?.message as string}
              />

              {/* Store Selector */}
              {showStoreDropdown && (
                <StoreDropdown
                  withNoneOption
                  defaultValue={defaultUser?.store}
                  setValue={setValue}
                  error={errors?.store?.message as string}
                  label="Store"
                  name="store"
                  required
                  hintText="The selected store's order will be displayed in the store order page"
                />
              )}
            </div>
            <div className="col-span-8 w-full md:col-span-4 md:pl-7">
              <label className="text-base font-bold text-sidebar-primary">
                Page Access Permission
                {!isUpdatePage && <span className="text-error">*</span>}
              </label>

              <div className="mt-3 flex w-full flex-col gap-2">
                {pageAccessPermissionOptions.map((permission) => (
                  <div
                    className="flex w-full items-center justify-between gap-2"
                    key={permission.value}
                  >
                    <p className="shrink-0 whitespace-nowrap text-sm">
                      {permission.label}
                    </p>
                    <div className="w-full max-w-36">
                      <Dropdown
                        className="h-8 text-sm"
                        size="small"
                        setValue={setValue}
                        name={`PAGE_${permission.value}`}
                        defaultValue={getValues(`PAGE_${permission.value}`)}
                        error={errors?.role?.message as string}
                        options={permissionOptions}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Actions : submit | cancle */}
          <div className="mt-6 flex w-72 gap-x-4">
            <Button
              type="submit"
              buttonText={submitButtonText}
              className="w-full"
            />

            <Button
              type="button"
              className="w-full"
              variant="outline"
              buttonText={'Cancel'}
              onClick={handleBack}
            />
          </div>
        </form>
      </FormLayout>
    </section>
  )
}

export default UserForm
